import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

const mkInitials = (name = "") => name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "U";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const STATUS_COLORS = {
  submitted:  { bg: "#E0F2F1", color: "#00897B" },
  processing: { bg: "#fefce8", color: "#ca8a04" },
  completed:  { bg: "#f0fdf4", color: "#16a34a" },
  cancelled:  { bg: "#fef2f2", color: "#dc2626" },
};

export default function RequisitionsPage() {
  const navigate = useNavigate();
  const [sideOpen, setSideOpen] = useState(true);
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ test_name: "", ordered_by: "", facility: "", notes: "" });
  const W = sideOpen ? "210px" : "50px";

  useEffect(() => {
    const token = localStorage.getItem("access");
    if (!token) { navigate("/login"); return; }
    fetchReqs();
  }, []);

  const fetchReqs = async () => {
    try {
      const res = await api.get("requisitions/");
      setReqs(res.data ?? []);
    } catch {
      setReqs([
        { id:1, test_name:"Complete Blood Count", ordered_by:"Dr. Sarah Johnson", facility:"Main Lab", barcode:"ML001234", status:"completed", date:"2026-03-01", notes:"" },
        { id:2, test_name:"Lipid Panel", ordered_by:"Dr. Michael Chen", facility:"Main Lab", barcode:"ML001235", status:"processing", date:"2026-03-08", notes:"Fasting required" },
        { id:3, test_name:"HbA1c", ordered_by:"Dr. Sarah Johnson", facility:"Main Lab", barcode:"ML001236", status:"submitted", date:"2026-03-10", notes:"" },
      ]);
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.test_name) { setError("Test name is required."); return; }
    setSaving(true);
    try {
      const res = await api.post("requisitions/", form);
      setReqs([res.data, ...reqs]);
      setShowForm(false);
      setForm({ test_name: "", ordered_by: "", facility: "", notes: "" });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit requisition.");
    } finally { setSaving(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    navigate("/login");
  };

  const submitted  = reqs.filter(r => r.status === "submitted").length;
  const processing = reqs.filter(r => r.status === "processing").length;
  const completed  = reqs.filter(r => r.status === "completed").length;

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", fontFamily:"Inter,sans-serif", color:"#6b7280", fontSize:13 }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:32, height:32, border:"2px solid #e5e7eb", borderTopColor:"#00897B", borderRadius:"50%", animation:"spin .7s linear infinite", margin:"0 auto 12px" }}></div>
        Loading requisitions…
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    </div>
  );

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Inter', sans-serif; background: #f1f5f9; color: #111; font-size: 13px; -webkit-font-smoothing: antialiased; }
        button { font-family: inherit; cursor: pointer; }
        .app { display: flex; min-height: 100vh; }
        .sb { width:${W}; background:#00897B; display:flex; flex-direction:column; position:fixed; top:0; left:0; bottom:0; z-index:200; transition:width .2s; overflow:hidden; }
        .sb-logo { height:56px; display:flex; align-items:center; padding:0 13px; gap:9px; border-bottom:1px solid rgba(255,255,255,0.1); flex-shrink:0; }
        .sb-logo-box { width:28px; height:28px; border-radius:7px; background:#fff; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .sb-logo-name { font-size:13px; font-weight:700; color:#fff; white-space:nowrap; opacity:${sideOpen?1:0}; transition:opacity .15s; }
        .sb-logo-sub { font-size:9.5px; color:rgba(255,255,255,0.45); white-space:nowrap; opacity:${sideOpen?1:0}; transition:opacity .15s; }
        .sb-nav { flex:1; padding:10px 7px; overflow-y:auto; scrollbar-width:none; }
        .sb-grp { font-size:9.5px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:rgba(255,255,255,0.3); padding:8px 8px 3px; white-space:nowrap; opacity:${sideOpen?1:0}; transition:opacity .15s; }
        .nb { width:100%; display:flex; align-items:center; gap:8px; padding:8px 9px; border-radius:7px; border:none; background:none; color:rgba(255,255,255,0.5); font-size:12.5px; font-weight:500; text-align:left; white-space:nowrap; margin-bottom:1px; transition:background .1s, color .1s; }
        .nb:hover { background:rgba(255,255,255,0.1); color:rgba(255,255,255,0.9); }
        .nb.on { background:rgba(255,255,255,0.15); color:#fff; font-weight:600; }
        .nav-ic { flex-shrink:0; width:15px; height:15px; display:flex; align-items:center; justify-content:center; }
        .nav-lbl { opacity:${sideOpen?1:0}; transition:opacity .15s; white-space:nowrap; }
        .sb-foot { border-top:1px solid rgba(255,255,255,0.1); padding:8px 7px; flex-shrink:0; }
        .sb-user { display:flex; align-items:center; gap:8px; padding:7px 9px; }
        .sb-av { width:28px; height:28px; border-radius:50%; background:rgba(255,255,255,0.18); display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; color:#fff; flex-shrink:0; }
        .sb-uinfo { opacity:${sideOpen?1:0}; transition:opacity .15s; overflow:hidden; }
        .sb-uname { font-size:12px; font-weight:600; color:#fff; white-space:nowrap; }
        .sb-urole { font-size:10px; color:rgba(255,255,255,0.35); white-space:nowrap; }
        .sb-out { width:100%; display:flex; align-items:center; gap:8px; padding:7px 9px; border-radius:7px; border:none; background:none; color:rgba(255,255,255,0.3); font-size:12px; margin-top:2px; transition:background .1s, color .1s; }
        .sb-out:hover { background:rgba(239,68,68,0.15); color:#fca5a5; }
        .sb-out-lbl { opacity:${sideOpen?1:0}; transition:opacity .15s; }
        .main { margin-left:${W}; flex:1; display:flex; flex-direction:column; transition:margin-left .2s; min-width:0; }
        .topbar { height:56px; background:#fff; border-bottom:1px solid #e5e7eb; display:flex; align-items:center; justify-content:space-between; padding:0 18px; position:sticky; top:0; z-index:100; flex-shrink:0; }
        .tb-l { display:flex; align-items:center; gap:10px; }
        .tb-toggle { width:30px; height:30px; border-radius:6px; border:1px solid #e5e7eb; background:none; display:flex; align-items:center; justify-content:center; color:#6b7280; transition:background .1s; }
        .tb-toggle:hover { background:#f3f4f6; }
        .tb-title { font-size:13.5px; font-weight:700; color:#0f172a; }
        .tb-sub { font-size:11px; color:#9ca3af; }
        .tb-r { display:flex; align-items:center; gap:7px; }
        .tb-badge { display:flex; align-items:center; gap:5px; padding:4px 10px; border-radius:20px; background:#E0F2F1; font-size:10.5px; font-weight:600; color:#00897B; }
        .tb-av { width:30px; height:30px; border-radius:50%; background:#00897B; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; color:#fff; }
        .content { padding:16px; flex:1; }
        .page-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; flex-wrap:wrap; gap:10px; }
        .page-title { font-size:20px; font-weight:700; color:#0f172a; margin-bottom:3px; }
        .page-sub { font-size:12px; color:#6b7280; }
        .btn-primary { padding:8px 16px; border-radius:7px; border:none; background:#00897B; font-size:12.5px; font-weight:600; color:#fff; transition:background .1s; }
        .btn-primary:hover { background:#00695C; }
        .stats { display:grid; grid-template-columns:repeat(4,1fr); gap:10px; margin-bottom:16px; }
        .stat { background:#fff; border:1px solid #e5e7eb; border-radius:10px; padding:13px 15px; }
        .stat-lbl { font-size:11px; color:#6b7280; font-weight:500; margin-bottom:6px; }
        .stat-n { font-size:26px; font-weight:700; color:#0f172a; line-height:1; }
        .req-list { display:flex; flex-direction:column; gap:8px; }
        .req-card { background:#fff; border:1px solid #e5e7eb; border-radius:10px; padding:14px 16px; display:flex; align-items:center; gap:14px; transition:border-color .15s; }
        .req-card:hover { border-color:#00897B; }
        .req-icon { width:38px; height:38px; border-radius:9px; background:#E0F2F1; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .req-icon svg { width:18px; height:18px; stroke:#00897B; fill:none; stroke-width:1.8; }
        .req-name { font-size:13px; font-weight:600; color:#0f172a; margin-bottom:2px; }
        .req-meta { font-size:11px; color:#9ca3af; }
        .req-barcode { font-size:10px; font-weight:600; padding:2px 8px; border-radius:20px; background:#f1f5f9; color:#475569; margin-left:6px; font-family:monospace; }
        .status-pill { font-size:10.5px; font-weight:600; padding:3px 10px; border-radius:20px; white-space:nowrap; }
        .empty-state { background:#fff; border:1px solid #e5e7eb; border-radius:10px; padding:48px 24px; text-align:center; }
        .empty-icon { width:48px; height:48px; border-radius:12px; background:#f1f5f9; display:flex; align-items:center; justify-content:center; margin:0 auto 14px; }
        .empty-title { font-size:14px; font-weight:600; color:#374151; margin-bottom:4px; }
        .empty-sub { font-size:12px; color:#9ca3af; }
        .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.4); z-index:500; display:flex; align-items:center; justify-content:center; padding:16px; }
        .modal { background:#fff; border-radius:14px; width:100%; max-width:480px; overflow:hidden; box-shadow:0 20px 60px rgba(0,0,0,0.2); }
        .modal-head { padding:16px 20px; border-bottom:1px solid #f3f4f6; display:flex; align-items:center; justify-content:space-between; }
        .modal-title { font-size:14px; font-weight:700; color:#0f172a; }
        .modal-close { width:28px; height:28px; border-radius:6px; border:1px solid #e5e7eb; background:none; display:flex; align-items:center; justify-content:center; color:#6b7280; }
        .modal-body { padding:20px; }
        .field { margin-bottom:12px; }
        .field label { display:block; font-size:11px; font-weight:500; color:#475569; margin-bottom:5px; }
        .field input, .field textarea { width:100%; padding:9px 12px; font-size:12px; border:1px solid #e2e8f0; border-radius:8px; background:#f8fafc; color:#0f172a; outline:none; font-family:inherit; }
        .field input:focus, .field textarea:focus { border-color:#00897B; box-shadow:0 0 0 3px rgba(0,137,123,0.1); background:#fff; }
        .field textarea { resize:vertical; min-height:72px; }
        .modal-foot { padding:14px 20px; border-top:1px solid #f3f4f6; display:flex; justify-content:flex-end; gap:8px; }
        .btn-cancel { padding:8px 16px; border-radius:7px; border:1px solid #e5e7eb; background:#f8fafc; font-size:12px; font-weight:500; color:#64748b; }
        .alert-err { background:#fef2f2; border:1px solid #fecaca; border-radius:8px; padding:9px 12px; font-size:11.5px; color:#dc2626; margin-bottom:12px; }
        .spinner { width:13px; height:13px; border:2px solid rgba(255,255,255,0.3); border-top-color:#fff; border-radius:50%; animation:spin .6s linear infinite; display:inline-block; }
        @keyframes spin { to { transform:rotate(360deg); } }
        @media (max-width:900px) { .stats { grid-template-columns:repeat(2,1fr); } }
        @media (max-width:600px) { .content { padding:10px; } }
      `}</style>

      <div className="app">
        {/* SIDEBAR */}
        <aside className="sb">
          <div className="sb-logo">
            <div className="sb-logo-box">
              <img src="/micronlabs.png" alt="MicronLabs" style={{ width:"18px", height:"18px", objectFit:"contain" }}/>
            </div>
            <div>
              <div className="sb-logo-name">MicronLabs</div>
              <div className="sb-logo-sub">Laboratory Information System</div>
            </div>
          </div>
          <nav className="sb-nav">
            <div className="sb-grp">Main</div>
            {[
              { id:"dashboard",     label:"Dashboard",      icon:<><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></> },
              { id:"requisitions",  label:"E-Requisitions", icon:<><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M14 14h7v7"/></> },
              { id:"results",       label:"Lab Results",    icon:<><path d="M9 3h6M9 3v8l-4 9h14l-4-9V3"/></> },
              { id:"prescriptions", label:"Prescriptions",  icon:<><path d="M10.5 20H4a2 2 0 01-2-2v-3.5a2 2 0 012-2h6.5"/><path d="M13.5 4H20a2 2 0 012 2v3.5a2 2 0 01-2 2h-6.5"/><path d="M10.5 20l9-16"/></> },
              { id:"records",       label:"Health Records", icon:<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8"/></> },
            ].map(({ id, label, icon }) => (
              <button key={id} className={`nb ${id==="requisitions"?"on":""}`} onClick={() => navigate(`/${id}`)}>
                <span className="nav-ic"><svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">{icon}</svg></span>
                <span className="nav-lbl">{label}</span>
              </button>
            ))}
            <div className="sb-grp" style={{marginTop:8}}>Account</div>
            {[
              { id:"messages", label:"Messages", icon:<><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></> },
              { id:"billing",  label:"Billing",  icon:<><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></> },
              { id:"profile",  label:"My Profile", icon:<><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></> },
            ].map(({ id, label, icon }) => (
              <button key={id} className="nb" onClick={() => navigate(`/${id}`)}>
                <span className="nav-ic"><svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">{icon}</svg></span>
                <span className="nav-lbl">{label}</span>
              </button>
            ))}
          </nav>
          <div className="sb-foot">
            <div className="sb-user">
              <div className="sb-av">U</div>
              <div className="sb-uinfo">
                <div className="sb-uname">My Account</div>
                <div className="sb-urole">Patient</div>
              </div>
            </div>
            <button className="sb-out" onClick={handleLogout}>
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>
              <span className="sb-out-lbl">Sign out</span>
            </button>
          </div>
        </aside>

        {/* MAIN */}
        <div className="main">
          <header className="topbar">
            <div className="tb-l">
              <button className="tb-toggle" onClick={() => setSideOpen(o => !o)}>
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16"/></svg>
              </button>
              <div>
                <div className="tb-title">E-Requisitions</div>
                <div className="tb-sub">{new Date().toLocaleDateString("en-US", { weekday:"long", month:"long", day:"numeric" })}</div>
              </div>
            </div>
            <div className="tb-r">
              <div className="tb-badge">
                <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/></svg>
                HIPAA secured
              </div>
              <div className="tb-av">U</div>
            </div>
          </header>

          <div className="content">
            <div className="page-head">
              <div>
                <div className="page-title">E-Requisitions</div>
                <div className="page-sub">Submit and track digital lab test orders</div>
              </div>
              <button className="btn-primary" onClick={() => setShowForm(true)}>+ New test order</button>
            </div>

            <div className="stats">
              <div className="stat"><div className="stat-lbl">Total orders</div><div className="stat-n">{reqs.length}</div></div>
              <div className="stat"><div className="stat-lbl">Submitted</div><div className="stat-n" style={{color:"#00897B"}}>{submitted}</div></div>
              <div className="stat"><div className="stat-lbl">Processing</div><div className="stat-n" style={{color:"#ca8a04"}}>{processing}</div></div>
              <div className="stat"><div className="stat-lbl">Completed</div><div className="stat-n" style={{color:"#16a34a"}}>{completed}</div></div>
            </div>

            <div className="req-list">
              {reqs.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <svg width="24" height="24" fill="none" stroke="#9ca3af" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M14 14h7v7"/></svg>
                  </div>
                  <div className="empty-title">No requisitions yet</div>
                  <div className="empty-sub">Submit your first test order to get started</div>
                </div>
              ) : reqs.map(r => {
                const sc = STATUS_COLORS[r.status] || { bg:"#f1f5f9", color:"#475569" };
                return (
                  <div key={r.id} className="req-card">
                    <div className="req-icon">
                      <svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M14 14h7v7"/></svg>
                    </div>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{display:"flex", alignItems:"center", flexWrap:"wrap", gap:4, marginBottom:3}}>
                        <span className="req-name">{r.test_name}</span>
                        {r.barcode && <span className="req-barcode">{r.barcode}</span>}
                      </div>
                      <div className="req-meta">
                        {r.ordered_by && <>{r.ordered_by} &nbsp;·&nbsp;</>}
                        {r.facility && <>{r.facility} &nbsp;·&nbsp;</>}
                        {fmtDate(r.date)}
                      </div>
                    </div>
                    <span className="status-pill" style={{ background: sc.bg, color: sc.color }}>
                      {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* NEW REQUISITION MODAL */}
      {showForm && (
        <div className="modal-overlay" onClick={(e) => e.target.className === "modal-overlay" && setShowForm(false)}>
          <div className="modal">
            <div className="modal-head">
              <span className="modal-title">New test order</span>
              <button className="modal-close" onClick={() => setShowForm(false)}>
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="alert-err">{error}</div>}
                <div className="field">
                  <label>Test name *</label>
                  <input type="text" placeholder="e.g. Complete Blood Count" value={form.test_name} onChange={e => setForm({...form, test_name: e.target.value})} required/>
                </div>
                <div className="field">
                  <label>Ordered by</label>
                  <input type="text" placeholder="Doctor's name" value={form.ordered_by} onChange={e => setForm({...form, ordered_by: e.target.value})}/>
                </div>
                <div className="field">
                  <label>Facility</label>
                  <input type="text" placeholder="Lab or facility name" value={form.facility} onChange={e => setForm({...form, facility: e.target.value})}/>
                </div>
                <div className="field">
                  <label>Notes</label>
                  <textarea placeholder="Any special instructions..." value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}/>
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? <span className="spinner"/> : "Submit order"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
